import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { toNumber } from '../utils/formatters';

export default function useDashboardData(dashboardMeta, getDashboard, downloadTemplate, importStaff) {
    const [pageLoader, setPageLoader] = useState(false);
    const [dashboardDetails, setDashboardDetails] = useState({});
    const [downloadLoader, setDownloadLoader] = useState(false);
    const [uploadLoader, setUploadLoader] = useState(false);
    const [addUserModal, setAddUserModal] = useState(false);

    useEffect(() => {
        if (dashboardMeta) {
            setDashboardDetails({ ...dashboardMeta });
        }
    }, [dashboardMeta]);

    useEffect(() => {
        if (getDashboard) {
            setPageLoader(true);
            const params = {
                userType: 'Student',
                status: 'Approved',
                limit: 10
            };
            getDashboard(params, () => setPageLoader(false));
        }
    }, [getDashboard]);

    const getTeacherCount = useCallback(() => {
        return toNumber(dashboardDetails?.roleWiseCountStats?.[1]?.totalCount);
    }, [dashboardDetails]);

    const getDataOperatorsCount = useCallback(() => {
        return toNumber(dashboardDetails?.roleWiseCountStats?.[2]?.totalCount);
    }, [dashboardDetails]);

    const getOperationsCount = useCallback(() => {
        return toNumber(dashboardDetails?.roleWiseCountStats?.[3]?.totalCount);
    }, [dashboardDetails]);

    const getTotalStaffCount = useCallback(() => {
        return getTeacherCount() + getDataOperatorsCount() + getOperationsCount();
    }, [getTeacherCount, getDataOperatorsCount, getOperationsCount]);

    const getOperationsPersonnelCount = useCallback(() => {
        return getDataOperatorsCount() + getOperationsCount();
    }, [getDataOperatorsCount, getOperationsCount]);

    const getTeacherLastAdded = useCallback(() => {
        return dashboardDetails?.roleWiseCountStats?.[1]?.lastAdded || 'N/A';
    }, [dashboardDetails]);

    const getOperationsLastAdded = useCallback(() => {
        const dataOpsLastAdded = dashboardDetails?.roleWiseCountStats?.[2]?.lastAdded;
        const opsLastAdded = dashboardDetails?.roleWiseCountStats?.[3]?.lastAdded;
        if (dataOpsLastAdded && opsLastAdded) {
            return dataOpsLastAdded > opsLastAdded ? dataOpsLastAdded : opsLastAdded;
        }
        return dataOpsLastAdded || opsLastAdded || 'N/A';
    }, [dashboardDetails]);

    const handleFileChange = useCallback((event, onComplete) => {
        const file = event.target.files[0];
        if (!file) return;

        const validExtensions = ['xls', 'xlsx', 'csv'];
        const fileExtension = file.name.split('.').pop().toLowerCase();

        if (!validExtensions.includes(fileExtension)) {
            message.error('Please upload a valid file format (xls, xlsx, csv)');
            event.target.value = '';
            return;
        }

        setUploadLoader(true);
        const formData = new FormData();
        formData.append('file', file);

        if (importStaff) {
            importStaff(formData, () => {
                setUploadLoader(false);
                message.success('Staff imported successfully!');
                const params = {
                    userType: 'Student',
                    status: 'Approved',
                    limit: 10
                };
                if (getDashboard) {
                    getDashboard(params, () => {
                        if (onComplete) onComplete();
                    });
                } else if (onComplete) {
                    onComplete();
                }
            }, (error) => {
                setUploadLoader(false);
                message.error(error?.message || 'Failed to import staff. Please try again.');
            });
        } else {
            setUploadLoader(false);
            message.error('Import action not available.');
        }

        event.target.value = '';
    }, [importStaff, getDashboard]);

    const handleDownloadTemplate = useCallback((e) => {
        e.stopPropagation();
        if (downloadTemplate) {
            setDownloadLoader(true);
            downloadTemplate(() => setDownloadLoader(false));
        } else {
            message.error('Download action not available.');
        }
    }, [downloadTemplate]);

    return {
        pageLoader,
        setPageLoader,
        dashboardDetails,
        downloadLoader,
        uploadLoader,
        setUploadLoader,
        addUserModal,
        setAddUserModal,
        getTeacherCount,
        getDataOperatorsCount,
        getOperationsCount,
        getTotalStaffCount,
        getOperationsPersonnelCount,
        getTeacherLastAdded,
        getOperationsLastAdded,
        handleFileChange,
        handleDownloadTemplate
    };
}
